import { useMemo, useState } from 'react'
import LaborLineChart from '@/components/payroll/LaborLineChart'
import {
  bucketTopNOther,
  colorForSeriesKey,
  filterSeriesByHiddenKeys,
  nextHiddenSeriesKeys,
  sharedLaborChartScale,
  unionSeriesColorKeys,
  withTotalSeriesLast,
  type LaborPctTooltipFormatters,
} from '@/components/payroll/laborChartSeries'
import { LABOR_STORE_NAMES, type LaborSeries, type LaborStoreName } from '@/services/laborAnalytics'

interface LaborStoreCompareProps {
  seriesByStore: Record<LaborStoreName, LaborSeries[]>
  ariaLabel: string
  valueFormatter?: (value: number) => string
  yAxisFormatter?: (value: number) => string
  bucketSeries?: boolean
  pctTooltip?: LaborPctTooltipFormatters
  loading?: boolean
  closedDatesByStore?: Record<LaborStoreName, ReadonlySet<string>>
}

function prepareSeries(series: LaborSeries[], bucketSeries: boolean): LaborSeries[] {
  const prepared = bucketSeries ? bucketTopNOther(series) : series
  return withTotalSeriesLast(prepared)
}

export default function LaborStoreCompare({
  seriesByStore,
  ariaLabel,
  valueFormatter,
  yAxisFormatter,
  bucketSeries = true,
  pctTooltip,
  loading = false,
  closedDatesByStore,
}: LaborStoreCompareProps) {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set())
  const [hoverDate, setHoverDate] = useState<string | null>(null)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [hoverYValue, setHoverYValue] = useState<number | null>(null)
  const [sourcePane, setSourcePane] = useState<LaborStoreName | null>(null)

  const clearHoverSync = () => {
    setHoverDate(null)
    setActiveKey(null)
    setHoverYValue(null)
    setSourcePane(null)
  }

  const preparedByStore = useMemo(
    () =>
      Object.fromEntries(
        LABOR_STORE_NAMES.map((store) => [store, prepareSeries(seriesByStore[store] ?? [], bucketSeries)]),
      ) as Record<LaborStoreName, LaborSeries[]>,
    [bucketSeries, seriesByStore],
  )

  const colorKeys = useMemo(
    () => unionSeriesColorKeys(LABOR_STORE_NAMES.map((store) => preparedByStore[store])),
    [preparedByStore],
  )

  const scale = useMemo(() => {
    const visibleSeries = LABOR_STORE_NAMES.map((store) =>
      filterSeriesByHiddenKeys(preparedByStore[store], hiddenKeys),
    )
    return sharedLaborChartScale(visibleSeries)
  }, [hiddenKeys, preparedByStore])

  const legendKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const store of LABOR_STORE_NAMES) {
      for (const entry of preparedByStore[store]) {
        keys.add(entry.key)
      }
    }
    return withTotalSeriesLast(
      [...keys].map((key) => ({ key, points: [] })),
    ).map((entry) => entry.key)
  }, [preparedByStore])

  const showLegend = legendKeys.length > 1

  return (
    <div className="flex min-h-[400px] flex-1 flex-col">
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-2">
        {LABOR_STORE_NAMES.map((store) => (
          <div
            key={store}
            className="relative flex h-full min-h-[400px] flex-col"
            aria-busy={loading || undefined}
          >
            <LaborLineChart
              series={seriesByStore[store] ?? []}
              ariaLabel={`${ariaLabel} — ${store}`}
              valueFormatter={valueFormatter}
              yAxisFormatter={yAxisFormatter}
              bucketSeries={bucketSeries}
              pctTooltip={pctTooltip}
              scale={scale}
              hideLegend
              colorKeys={colorKeys}
              hiddenKeys={hiddenKeys}
              onHiddenKeysChange={setHiddenKeys}
              syncHoverDate={hoverDate}
              onHoverDate={(date) => {
                if (date == null) clearHoverSync()
                else setHoverDate(date)
              }}
              activeKey={activeKey}
              onActiveKeyChange={setActiveKey}
              syncHoverYValue={hoverYValue}
              onHoverYValueChange={setHoverYValue}
              paneId={store}
              syncSourcePane={sourcePane}
              onSourcePaneChange={setSourcePane}
              paneLabel={store}
              closedDates={closedDatesByStore?.[store]}
            />
            {loading ? (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                <p className="text-sm text-gray-500">Loading</p>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {showLegend ? (
        <div className="mt-3 flex shrink-0 flex-wrap gap-3 text-xs text-gray-600">
          {legendKeys.map((key) => {
            const hidden = hiddenKeys.has(key)
            const dimmed = !hidden && activeKey != null && activeKey !== key
            const color = colorForSeriesKey(key, colorKeys.indexOf(key))
            return (
              <button
                key={key}
                type="button"
                aria-pressed={!hidden}
                title={hidden ? `Show ${key}` : `Hide ${key}`}
                className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-opacity ${
                  hidden ? 'opacity-35' : dimmed ? 'opacity-40' : ''
                }`}
                onClick={() => {
                  setHiddenKeys((current) =>
                    nextHiddenSeriesKeys(current, key, legendKeys),
                  )
                }}
                onMouseEnter={() => {
                  if (!hidden) setActiveKey(key)
                }}
                onMouseLeave={() => setActiveKey(null)}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: color }}
                />
                <span
                  className={`max-w-[10rem] truncate ${hidden ? 'line-through' : ''}`}
                  title={key}
                >
                  {key}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
